/**
 * File Upload Modal Component
 * Phase 1: Structured File Attachment System
 * 
 * Features:
 * - Mandatory file category selection
 * - File type validation per category
 * - Drag & drop support
 * - Progress indicator
 */

import React, { useState, useRef, useCallback } from 'react';
import { MLS_SECTIONS, UTILITY_TYPES } from './fileClassification';
import { X, Upload, FileText, Image, Mic, HelpCircle, File, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { isSearchProduct } from '../../utils/orderHelpers';

// File Categories Configuration
// NOTE: Categories have been standardized as of Mar 2026:
// - "Audio Recording" (audio) is the single category for all phone/voice evidence
// - "Image / Screenshot" (image) is used for online source documents
// - Removed: call_recording, source_document, other (consolidated into existing categories)
export const FILE_CATEGORIES = {
  // TITLEFLOW-ORDER-ENTRY-DOCUMENTS-001 — client-provided previous title search; internal reference only
  prior_search_package: {
    label: 'Prior Search Package',
    icon: '🗂️',
    description: 'Previous title search provided by the client — internal reference only, never delivered',
    allowedTypes: [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ],
    allowedExtensions: ['.pdf', '.doc', '.docx'],
    maxSize: 15 * 1024 * 1024,
  },
  final_document: {
    label: 'Final Document',
    icon: '📄',
    description: 'Official final documents for the order',
    allowedTypes: ['application/pdf'],
    allowedExtensions: ['.pdf'],
    maxSize: 15 * 1024 * 1024, // 15MB
  },
  supporting_document: {
    label: 'Supporting Document',
    icon: '📑',
    description: 'Additional supporting documentation',
    allowedTypes: [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
    ],
    allowedExtensions: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv'],
    maxSize: 15 * 1024 * 1024,
  },
  // ============================================================================
  // SOURCE EVIDENCE CATEGORIES - For Source Evidence Validation
  // NOTE: "Audio Recording" (audio) is now the standard for Calling evidence
  // NOTE: "Image / Screenshot" (image) is now the standard for Online evidence
  // ============================================================================
  email_evidence: {
    label: 'Email Evidence',
    icon: '📧',
    description: 'Email correspondence as evidence',
    allowedTypes: ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'message/rfc822', 'application/vnd.ms-outlook'],
    allowedExtensions: ['.pdf', '.png', '.jpg', '.jpeg', '.eml', '.msg'],
    maxSize: 15 * 1024 * 1024,
    isEvidenceCategory: true,
    evidenceType: 'document',
  },
  fax_evidence: {
    label: 'Fax Evidence',
    icon: '📠',
    description: 'Fax confirmation or document as evidence',
    allowedTypes: ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/tiff'],
    allowedExtensions: ['.pdf', '.png', '.jpg', '.jpeg', '.tif', '.tiff'],
    maxSize: 15 * 1024 * 1024,
    isEvidenceCategory: true,
    evidenceType: 'document',
  },
  mail_evidence: {
    label: 'Mail Evidence',
    icon: '📬',
    description: 'Mail correspondence or delivery confirmation as evidence',
    allowedTypes: ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'],
    allowedExtensions: ['.pdf', '.png', '.jpg', '.jpeg'],
    maxSize: 15 * 1024 * 1024,
    isEvidenceCategory: true,
    evidenceType: 'document',
  },
  // ============================================================================
  internal_note: {
    label: 'Internal Note',
    icon: '📝',
    description: 'Internal notes and memos',
    allowedTypes: [
      'text/plain',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ],
    allowedExtensions: ['.txt', '.doc', '.docx'],
    maxSize: 10 * 1024 * 1024,
  },
  image: {
    label: 'Image / Screenshot',
    icon: '🖼',
    description: 'Images, screenshots, and online source documents',
    allowedTypes: ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'],
    allowedExtensions: ['.png', '.jpg', '.jpeg', '.pdf'],
    maxSize: 15 * 1024 * 1024,
    isEvidenceCategory: true,  // Also used for Online source evidence
    evidenceType: 'image',
  },
  audio: {
    label: 'Audio Recording',
    icon: '🎧',
    description: 'Audio recordings, voice memos, and call recordings',
    allowedTypes: ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav', 'audio/m4a', 'audio/x-m4a', 'audio/mp4'],
    allowedExtensions: ['.mp3', '.wav', '.m4a'],
    maxSize: 50 * 1024 * 1024, // 50MB for audio recordings
    isEvidenceCategory: true,  // Also used for Calling source evidence
    evidenceType: 'audio',
  },
  clarification_response: {
    label: 'Clarification Response',
    icon: '❓',
    description: 'Documents responding to clarification requests',
    allowedTypes: ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'],
    allowedExtensions: ['.pdf', '.png', '.jpg', '.jpeg'],
    maxSize: 15 * 1024 * 1024,
  },
};

// Role-based category permissions
// Defines which roles can upload to which categories
// NOTE: Categories standardized Mar 2026:
// - "audio" is the single category for Calling/phone evidence (replaces call_recording)
// - "image" is used for Online source evidence (replaces source_document)
// - "other" category removed (use supporting_document instead)
export const ROLE_CATEGORY_PERMISSIONS = {
  admin: ['prior_search_package', 'final_document', 'supporting_document', 'email_evidence', 'fax_evidence', 'mail_evidence', 'internal_note', 'image', 'audio', 'clarification_response'],
  processor: ['final_document', 'supporting_document', 'email_evidence', 'fax_evidence', 'mail_evidence', 'internal_note', 'image', 'audio', 'clarification_response'],
  title_search_processor: ['final_document', 'supporting_document', 'email_evidence', 'fax_evidence', 'mail_evidence', 'internal_note', 'image', 'audio', 'clarification_response'],
  search_processor: ['supporting_document', 'email_evidence', 'fax_evidence', 'mail_evidence', 'internal_note', 'image', 'audio', 'clarification_response'], // Cannot upload final_document
  tax_search_team_leader: ['final_document', 'supporting_document', 'email_evidence', 'fax_evidence', 'mail_evidence', 'internal_note', 'image', 'audio', 'clarification_response'],
  title_search_team_leader: ['prior_search_package', 'final_document', 'supporting_document', 'email_evidence', 'fax_evidence', 'mail_evidence', 'internal_note', 'image', 'audio', 'clarification_response'],
  team_leader: ['final_document', 'supporting_document', 'email_evidence', 'fax_evidence', 'mail_evidence', 'internal_note', 'image', 'audio', 'clarification_response'],
  qc_specialist: ['final_document', 'supporting_document', 'email_evidence', 'fax_evidence', 'mail_evidence', 'internal_note', 'image', 'audio', 'clarification_response'],
  order_desk: ['prior_search_package'], // TITLEFLOW-ORDER-ENTRY-DOCUMENTS-001: Order Desk uploads only the Prior Search Package
  accounting: ['supporting_document', 'internal_note'], // Very limited
  vendor_manager: ['supporting_document', 'internal_note', 'image'],
};

// Get categories available for an order status and user role
//
// May 1, 2026 — `clientProduct` (optional) lets us hide categories
// that aren't relevant to Title Search workflows. Search orders don't
// deliver a final document to the client and don't capture email/fax/
// mail/audio evidence the way Tax orders do, so the Upload Files modal
// was being cluttered with unusable options.
//
// Contract:
//   • isSearchProduct(clientProduct) === true → hide final_document,
//     email_evidence, fax_evidence, mail_evidence, audio.
//   • Otherwise, no product-level filtering — existing role +
//     status-based filtering still applies.
const SEARCH_EXCLUDED_CATEGORIES = new Set([
  'final_document',
  'email_evidence',
  'fax_evidence',
  'mail_evidence',
  'audio',
]);

export const getAvailableCategories = (orderStatus, userRole = 'processor', clientProduct = null) => {
  const allCategories = Object.keys(FILE_CATEGORIES);
  
  // Get role-based allowed categories
  const roleAllowedCategories = ROLE_CATEGORY_PERMISSIONS[userRole] || ROLE_CATEGORY_PERMISSIONS.processor;
  
  // Filter by role permissions first
  let availableCategories = allCategories.filter(cat => roleAllowedCategories.includes(cat));
  
  // Clarification Response only available when order is in clarification status
  if (orderStatus !== 'clarification') {
    availableCategories = availableCategories.filter(cat => cat !== 'clarification_response');
  }

  // Search-product-specific filter (May 1, 2026).
  if (isSearchProduct(clientProduct)) {
    availableCategories = availableCategories.filter(
      (cat) => !SEARCH_EXCLUDED_CATEGORIES.has(cat)
    );
  }
  
  return availableCategories;
};

// Validate file against category rules
export const validateFileForCategory = (file, categoryKey) => {
  const category = FILE_CATEGORIES[categoryKey];
  if (!category) {
    return { valid: false, error: 'Invalid category selected' };
  }

  // Check file size
  if (file.size > category.maxSize) {
    const maxSizeMB = Math.round(category.maxSize / (1024 * 1024));
    return { valid: false, error: `File size exceeds ${maxSizeMB}MB limit for ${category.label}` };
  }

  // Check file type
  const extension = '.' + file.name.split('.').pop().toLowerCase();
  const isValidType = category.allowedTypes.includes(file.type) || 
                      category.allowedExtensions.includes(extension);

  if (!isValidType) {
    return { 
      valid: false, 
      error: `Invalid file type for ${category.label}. Allowed: ${category.allowedExtensions.join(', ')}` 
    };
  }

  return { valid: true };
};

const FileUploadModal = ({ 
  isOpen, 
  onClose, 
  onUpload, 
  orderStatus,
  userRole = 'processor',
  clientProduct = null,  // May 1, 2026 — used to hide Search-irrelevant categories
  uploading = false,
  initialCategory = '', // Pre-select a category when opening from Smart Evidence Prompt
  existingFileCount = 0, // SEARCH-FLOW-002: active files already on the order
  supportingDocCount = 0, // TSP-SUPPORT-LIMIT-001: active Supporting Documents on the order
  isDualMlsOrder = false, // MLS-DOC-CLASS-001: MLS + Tax dual order → mandatory Tax / MLS choice
  postCompletion = false // COMPLETED-ORDER-DOC-UPLOAD: reference-only attachment to a Completed order (note required)
}) => {
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [postCompletionNote, setPostCompletionNote] = useState('');
  const postCompletionOk = !postCompletion || postCompletionNote.trim().length >= 5;
  const [belongsTo, setBelongsTo] = useState('');        // '' | 'tax' | 'mls' — deliberately NO default
  const [mlsSection, setMlsSection] = useState('');
  const [mlsUtilityType, setMlsUtilityType] = useState('');
  const classificationOk = !isDualMlsOrder || belongsTo === 'tax' || (belongsTo === 'mls' && !!mlsSection);
  const classification = isDualMlsOrder
    ? { component: belongsTo, section: belongsTo === 'mls' ? mlsSection : '', utility_type: belongsTo === 'mls' && mlsSection === 'utility_accounts' ? mlsUtilityType : '' }
    : null;
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const fileInputRef = useRef(null);

  const availableCategories = getAvailableCategories(orderStatus, userRole, clientProduct);
  
  // Update selected category when initialCategory prop changes (e.g., opening from different source)
  React.useEffect(() => {
    if (isOpen && initialCategory && availableCategories.includes(initialCategory)) {
      setSelectedCategory(initialCategory);
    }
  }, [isOpen, initialCategory, availableCategories]);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (!selectedCategory) {
      setValidationErrors([{ file: 'Files', error: 'Please select a category first' }]);
      return;
    }

    const droppedFiles = Array.from(e.dataTransfer.files);
    validateAndSetFiles(droppedFiles);
  }, [selectedCategory]);

  const validateAndSetFiles = (files) => {
    // SEARCH-FLOW-002 (Aug 2026): Search Processor allowance — max 5
    // attachments per order, 15MB per file (per-category rules below).
    if (userRole === 'search_processor') {
      const remaining = 5 - existingFileCount - selectedFiles.length;
      if (remaining <= 0) {
        setValidationErrors([{ file: 'Attachment limit', error: 'Search Processors may upload a maximum of 5 attachments per order.' }]);
        return;
      }
      if (files.length > remaining) {
        files = files.slice(0, remaining);
        setValidationErrors([{ file: 'Attachment limit', error: `Only ${remaining} more attachment${remaining === 1 ? '' : 's'} allowed (max 5 per order).` }]);
      }
    }
    // TITLE-SUPPORT-LIMIT-002 (Aug 2026): Title Search ORDERS — max 5
    // Supporting Documents per order (existing + newly selected), any role.
    // Over-limit selections are rejected as a WHOLE batch (no silent
    // truncation) with an explicit toast. Mirrored by the upload API.
    const isTitleSearchOrder = clientProduct && !/tax|mls|utility|municipal|hoa/i.test(clientProduct);
    if (isTitleSearchOrder && selectedCategory === 'supporting_document') {
      const existing = supportingDocCount + selectedFiles.length;
      const remaining = 5 - existing;
      if (remaining <= 0) {
        const msg = 'Maximum 5 supporting documents are allowed for Title Search orders. Please remove an existing file before uploading another.';
        toast.error(msg);
        setValidationErrors([{ file: 'Supporting Documents limit', error: msg }]);
        return;
      }
      if (files.length > remaining) {
        const excess = files.length - remaining;
        const msg = existing > 0
          ? `This order already has ${existing} supporting document${existing === 1 ? '' : 's'}. You can upload only ${remaining} more file${remaining === 1 ? '' : 's'}. Please reduce your selection.`
          : `Maximum 5 supporting documents are allowed for Title Search orders. You selected ${files.length} files, but only ${remaining} can be uploaded. Please remove ${excess} file${excess === 1 ? '' : 's'} and try again.`;
        toast.error(msg);
        setValidationErrors([{ file: 'Supporting Documents limit', error: msg }]);
        return;
      }
    }
    if (!selectedCategory) {
      setValidationErrors([{ file: 'Files', error: 'Please select a category first' }]);
      return;
    }

    const errors = [];
    const validFiles = [];

    files.forEach(file => {
      const validation = validateFileForCategory(file, selectedCategory);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        errors.push({ file: file.name, error: validation.error });
      }
    });

    setValidationErrors(errors);
    setSelectedFiles(validFiles);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    validateAndSetFiles(files);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setSelectedFiles([]);
    // TITLE-SUPPORT-LIMIT-002 — immediate feedback when the limit is already hit
    if (category === 'supporting_document'
        && clientProduct && !/tax|mls|utility|municipal|hoa/i.test(clientProduct)
        && supportingDocCount >= 5) {
      const msg = 'Maximum 5 supporting documents are allowed for Title Search orders. Please remove an existing file before uploading another.';
      toast.error(msg);
      setValidationErrors([{ file: 'Supporting Documents limit', error: msg }]);
    } else {
      setValidationErrors([]);
    }
  };

  const handleUpload = () => {
    if (!selectedCategory || selectedFiles.length === 0 || !classificationOk || !postCompletionOk) return;
    onUpload(selectedFiles, selectedCategory, classification, postCompletionNote.trim());
  };

  const handleClose = () => {
    setSelectedCategory('');
    setPostCompletionNote('');
    setSelectedFiles([]);
    setValidationErrors([]);
    onClose();
  };

  const getCategoryIcon = (categoryKey) => {
    const iconMap = {
      final_document: <FileText className="w-5 h-5 text-red-600" />,
      supporting_document: <FileText className="w-5 h-5 text-blue-600" />,
      internal_note: <FileText className="w-5 h-5 text-gray-600" />,
      image: <Image className="w-5 h-5 text-green-600" />,
      audio: <Mic className="w-5 h-5 text-purple-600" />,
      clarification_response: <HelpCircle className="w-5 h-5 text-orange-600" />,
      other: <File className="w-5 h-5 text-slate-600" />,
    };
    return iconMap[categoryKey] || <File className="w-5 h-5" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!isOpen) return null;

  const selectedCategoryConfig = FILE_CATEGORIES[selectedCategory];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
          <div>
            <h2 className="text-lg font-bold text-slate-900">{postCompletion ? 'Add Document to Completed Order' : 'Upload Files'}</h2>
            <p className="text-sm text-slate-500 mt-0.5">{postCompletion ? 'Reference only — the order stays Completed and nothing is delivered or resent.' : 'Select a category and upload your files'}</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="close-upload-modal"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Category Selection */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-slate-700 mb-3">
              File Category <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {availableCategories.map((categoryKey) => {
                const category = FILE_CATEGORIES[categoryKey];
                const isSelected = selectedCategory === categoryKey;
                
                return (
                  <button
                    key={categoryKey}
                    type="button"
                    onClick={() => handleCategoryChange(categoryKey)}
                    className={`
                      flex flex-col items-center p-4 rounded-xl border-2 transition-all
                      ${isSelected 
                        ? 'border-purple-500 bg-purple-50 shadow-md' 
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }
                    `}
                    data-testid={`category-${categoryKey}`}
                  >
                    <span className="text-2xl mb-2">{category.icon}</span>
                    <span className={`text-sm font-medium ${isSelected ? 'text-purple-700' : 'text-slate-700'}`}>
                      {category.label}
                    </span>
                    {isSelected && (
                      <CheckCircle className="w-4 h-4 text-purple-600 mt-1" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Allowed formats hint */}
          {selectedCategoryConfig && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <span className="font-semibold">Allowed formats:</span>{' '}
                {selectedCategoryConfig.allowedExtensions.join(', ').toUpperCase()}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Max size: {Math.round(selectedCategoryConfig.maxSize / (1024 * 1024))}MB
              </p>
            </div>
          )}

          {/* Drop Zone */}
          <div
            className={`
              relative border-2 border-dashed rounded-xl p-8 text-center transition-all
              ${!selectedCategory 
                ? 'border-slate-200 bg-slate-50 opacity-50 cursor-not-allowed' 
                : dragActive 
                  ? 'border-purple-500 bg-purple-50' 
                  : 'border-slate-300 bg-white hover:border-purple-400 hover:bg-purple-50/50 cursor-pointer'
              }
            `}
            onDragEnter={selectedCategory ? handleDragEnter : undefined}
            onDragLeave={selectedCategory ? handleDragLeave : undefined}
            onDragOver={selectedCategory ? handleDragOver : undefined}
            onDrop={selectedCategory ? handleDrop : undefined}
            onClick={() => selectedCategory && fileInputRef.current?.click()}
          >
            <Upload className={`w-12 h-12 mx-auto mb-4 ${selectedCategory ? 'text-slate-400' : 'text-slate-300'}`} />
            <p className={`text-sm font-medium ${selectedCategory ? 'text-slate-700' : 'text-slate-400'}`}>
              {!selectedCategory 
                ? 'Select a category first'
                : dragActive 
                  ? 'Drop files here...'
                  : 'Drag & drop files here, or click to browse'
              }
            </p>
            {selectedCategory && (
              <p className="text-xs text-slate-500 mt-2">
                {selectedCategoryConfig?.description}
              </p>
            )}
            
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              accept={selectedCategoryConfig?.allowedExtensions.join(',')}
              onChange={handleFileSelect}
              disabled={!selectedCategory}
            />
          </div>

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              {validationErrors.map((error, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span><strong>{error.file}:</strong> {error.error}</span>
                </div>
              ))}
            </div>
          )}

          {/* Selected Files */}
          {selectedFiles.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-2">
                Selected Files ({selectedFiles.length})
              </h4>
              <div className="space-y-2">
                {selectedFiles.map((file, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg"
                  >
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{file.name}</p>
                      <p className="text-xs text-slate-500">{formatFileSize(file.size)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFiles(prev => prev.filter((_, i) => i !== idx));
                      }}
                      className="p-1 hover:bg-red-100 rounded transition-colors"
                    >
                      <X className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {isDualMlsOrder && (
          <div className="px-6 pb-4" data-testid="upload-belongs-to-block">
            <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-3 space-y-2">
              <div className="text-xs font-semibold text-slate-700">Belongs to <span className="text-rose-600">*</span>
                <span className="ml-1 font-normal text-slate-500">— MLS + Tax order: choose where this document belongs (no default)</span></div>
              <div className="flex gap-2">
                {[['tax', 'Tax'], ['mls', 'MLS']].map(([k, label]) => (
                  <button key={k} type="button" onClick={() => { setBelongsTo(k); setMlsSection(''); setMlsUtilityType(''); }} data-testid={`upload-belongs-${k}`}
                    className={`h-8 rounded-md border px-4 text-sm font-semibold transition-colors ${belongsTo === k ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}>
                    {label}
                  </button>
                ))}
              </div>
              {belongsTo === 'mls' && (
                <div className="flex gap-2">
                  <select value={mlsSection} onChange={(e) => { setMlsSection(e.target.value); setMlsUtilityType(''); }} data-testid="upload-mls-section"
                    className="h-8 flex-1 rounded-md border border-slate-300 bg-white px-2 text-sm">
                    <option value="">MLS Section * …</option>
                    {MLS_SECTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                  </select>
                  {mlsSection === 'utility_accounts' && (
                    <select value={mlsUtilityType} onChange={(e) => setMlsUtilityType(e.target.value)} data-testid="upload-mls-utility"
                      className="h-8 w-36 rounded-md border border-slate-300 bg-white px-2 text-sm">
                      <option value="">Utility type…</option>
                      {UTILITY_TYPES.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                    </select>
                  )}
                </div>
              )}
              {!classificationOk && belongsTo && <p className="text-[11px] text-rose-600" data-testid="upload-mls-section-required">MLS Section is required.</p>}
            </div>
          </div>
        )}
        {postCompletion && (
          <div className="px-6 pb-4" data-testid="post-completion-note-block">
            <label className="block text-sm font-medium text-slate-700 mb-1">Reason / Note <span className="text-rose-600">*</span></label>
            <textarea value={postCompletionNote} onChange={(e) => setPostCompletionNote(e.target.value)} rows={2} maxLength={500}
              placeholder="e.g., Client requested additional mortgage document after completion."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              data-testid="post-completion-note-input" />
            <p className="mt-1 text-[11px] text-slate-500">Recorded in the order timeline with your name, role, date/time, filename and category. Minimum 5 characters.</p>
          </div>
        )}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!selectedCategory || selectedFiles.length === 0 || uploading || !classificationOk || !postCompletionOk}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="upload-files-btn"
          >
            {uploading ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                {postCompletion ? 'Add Document' : 'Upload'} {selectedFiles.length > 0 ? `(${selectedFiles.length})` : ''}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FileUploadModal;
